import { format, subMonths, subYears } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import {
  BookingDataResponse,
  BookingManagementEnum,
  Companion,
  CompanionEnum,
} from '../../components/BookingWidget/BookingManagment/bookingManagment.interface';
import { ApiDateFormat } from '../consts/app.const';
import {
  FormQuotationInformationTraveller,
  FormQuotationInformationTravellerEnum,
} from '../../components/QuotationInformationForm/quoation-information.interface';
import {
  BookingConfig,
  BookingConfigEnum,
} from '../../components/BookingWidget/bookingSteps.interface';
import {
  isTravellerAgeAndBirthdate,
  TravellerAgeAndBirthdate,
} from '../../components/QuotationFormWeb/quotationFormWeb.types';

export const getDateOfBirth = (age: string): string => {
  if (
    age === undefined ||
    age === null ||
    Number.isNaN(Number(age)) ||
    Number(age) < 0
  ) {
    throw new Error(`Invalid age ${age}. It can not be converted to DoB.`);
  }

  const parsedAge = Number(age);
  let startPointdate = dayjs();

  // Workaround for PS. It returns error when DOB = today date,
  // so just subtrract 1 day
  if (parsedAge === 0) {
    startPointdate = startPointdate.subtract(1, 'day');
  }
  // End of workaround
  const dob = startPointdate
    .subtract(parsedAge, 'year')
    .subtract(6, 'month')
    .toDate();
  return format(dob, 'yyyy-MM-dd');
};

export const getCompanionId = (
  companions: Companion[],
  index: number,
  bookingDataResponse: BookingDataResponse | null,
): number | undefined => {
  if (
    bookingDataResponse &&
    bookingDataResponse[BookingManagementEnum.Companions] &&
    bookingDataResponse[BookingManagementEnum.Companions][index]
  ) {
    return +bookingDataResponse[BookingManagementEnum.Companions][index][
      CompanionEnum.Id
    ]!;
  }
  return undefined;
};

export const getCompanionsAge = (
  travellerAgeAndBirthdates: TravellerAgeAndBirthdate[],
  bookingDataResponse: BookingDataResponse | null,
): Companion[] => {
  const travellersAge: Companion[] = [];
  travellerAgeAndBirthdates.forEach(
    (
      travellerAgeAndBirthdate: TravellerAgeAndBirthdate,
      index: number,
    ): void => {
      travellersAge.push({
        [CompanionEnum.DateOfBirth]: travellerAgeAndBirthdate.dateOfBirth
          ? travellerAgeAndBirthdate.dateOfBirth.format('YYYY-MM-DD')
          : getDateOfBirth(travellerAgeAndBirthdate.age),
        [CompanionEnum.Age]: +travellerAgeAndBirthdate.age,
        [CompanionEnum.IsCustomer]: index === 0,
      });
      const companionId = getCompanionId(
        travellersAge,
        index,
        bookingDataResponse,
      );
      if (companionId) {
        travellersAge[index][CompanionEnum.Id] = companionId;
      }
    },
  );
  return travellersAge;
};

export const getTravellersAge = (
  ageRanges: TravellerAgeAndBirthdate[] | string[],
): { birthDate: string }[] => {
  const travellersAge: { birthDate: string; age?: string }[] = [];

  ageRanges.forEach((range: TravellerAgeAndBirthdate | string) => {
    if (isTravellerAgeAndBirthdate(range)) {
      if (range.dateOfBirth) {
        travellersAge.push({
          birthDate: range.dateOfBirth.format('YYYY-MM-DD'),
        });
      }
    } else if (typeof range === 'string') {
      travellersAge.push({ birthDate: getDateOfBirth(range) });
    }
  });

  return travellersAge;
};

export const getBirthdateFromAge = (
  age: string | undefined,
  birthdayFormat: string,
): string => {
  const currentDate = new Date();
  const birthdate = subMonths(subYears(currentDate, +age!), 6);
  return format(birthdate, birthdayFormat);
};

export const getTravellerAgeFromDate = (
  birthDate: Dayjs | undefined,
  bookingDate = new Date().toISOString(),
): number => {
  if (!birthDate) {
    return 0;
  }

  const bookingDayString = dayjs(bookingDate).format('YYYY-MM-DD');
  const birthDayString = dayjs(birthDate).format('YYYY-MM-DD');
  const ageDiffFromStrings = dayjs(bookingDayString).diff(
    dayjs(birthDayString),
    'year',
  );

  return ageDiffFromStrings;
};

export const getAgeForTraveller = (
  travellerAge: string | Dayjs | undefined,
): number =>
  dayjs.isDayjs(travellerAge)
    ? getTravellerAgeFromDate(travellerAge)
    : +travellerAge!;

export const getBirthdateForTraveller = (
  travellerAge: string | Dayjs | undefined,
): string => {
  if (!travellerAge) return '';
  return dayjs.isDayjs(travellerAge)
    ? format((travellerAge as Dayjs).toDate(), ApiDateFormat)
    : getBirthdateFromAge(travellerAge, ApiDateFormat);
};

export const getTravellerAge = (
  traveller: FormQuotationInformationTraveller,
  bookingConfigData: BookingConfig,
): string | number | undefined => {
  const travellerAge = traveller[FormQuotationInformationTravellerEnum.Age];
  const travellerBirthDate =
    traveller[FormQuotationInformationTravellerEnum.BirthDate];
  if (!travellerAge && travellerBirthDate) {
    return getTravellerAgeFromDate(travellerBirthDate as unknown as Dayjs);
  }
  return typeof travellerAge === 'string' && travellerAge.includes('/')
    ? dayjs().diff(
        dayjs(
          travellerAge,
          bookingConfigData[BookingConfigEnum.BirthdayFormat],
        ),
        'year',
      )
    : traveller.age;
};

export const getAgeByDayOfBirth = (
  age: string,
  dateOfBirth: Dayjs | string | undefined,
  bookingDate: string,
): { age: string; dateOfBirth: Dayjs | null } => {
  const calculateAge = (): string => {
    if (dateOfBirth && dayjs(dateOfBirth).isValid()) {
      return dayjs(bookingDate ?? new Date())
        .diff(dateOfBirth, 'year')
        .toString();
    }
    return age;
  };

  // Don't infer date of birth! It has to be always manually entered by user.
  const calculateDateOfBirth = (): Dayjs | null =>
    dateOfBirth ? dayjs(dateOfBirth) : null;

  return {
    age: calculateAge(),
    dateOfBirth: calculateDateOfBirth(),
  };
};

export default getTravellersAge;
